import type {
  ChevronProps,
  ClassNames,
  DayButtonProps,
  DayPickerProps,
  RootProps,
  WeekNumberProps,
} from "react-day-picker";

import type { TIconKey } from "@/ui/components/primitives/Icon";
import type { DefinedDefaults } from "@/ui/lib/types";

/*-------------------------------------
|  Constants
|-------------------------------------*/

export const CALENDAR_CLASS_MAP = {
  root: "w-fit bg-sys-white p-3 font-sys-default text-sys-black-850",
  months: "relative flex flex-col gap-4 md:flex-row",
  month: "flex w-full flex-col gap-4",
  nav: "absolute inset-x-0 top-0 flex w-full items-center justify-between",
  button_previous: "inline-flex h-8 w-8 select-none items-center justify-center rounded-sys-md text-sys-black-850 transition-colors hover:bg-sys-chartreuse-25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sys-chartreuse-600 disabled:pointer-events-none disabled:text-sys-grey-200",
  button_next: "inline-flex h-8 w-8 select-none items-center justify-center rounded-sys-md text-sys-black-850 transition-colors hover:bg-sys-chartreuse-25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sys-chartreuse-600 disabled:pointer-events-none disabled:text-sys-grey-200",
  month_caption: "flex h-8 w-full items-center justify-center px-9",
  dropdowns: "flex h-8 items-center justify-center gap-1 text-sys-black-850",
  dropdown_root: "relative inline-flex rounded-sys-md border border-sys-grey-200 shadow-xs focus-within:border-sys-chartreuse-600 focus-within:ring-2 focus-within:ring-sys-chartreuse-25 data-[disabled=true]:opacity-70",
  dropdown: "absolute inset-0 z-10 cursor-pointer opacity-0",
  caption_label: "flex select-none items-center justify-center gap-1 px-2 font-sys-display text-sys-base font-bold leading-sys-1",
  month_grid: "w-full border-collapse",
  weekdays: "flex",
  weekday: "flex-1 select-none text-center text-sys-xs font-normal leading-sys-1-3 text-sys-grey-500",
  week: "mt-1 flex w-full",
  day: "group/day relative h-8 w-8 p-0 text-center",
  week_number_header: "h-8 w-8 select-none text-center text-sys-xs font-normal leading-sys-1-3 text-sys-grey-500",
  week_number: "h-8 w-8 select-none text-center text-sys-xs text-sys-grey-500",
  hidden: "invisible",
} as const satisfies Partial<ClassNames>;

export const CALENDAR_DAY_BUTTON_CLASS_MAP = {
  base: "flex h-8 w-8 select-none items-center justify-center rounded-sys-md text-sys-sm leading-sys-1-3 text-sys-black-850 transition-colors hover:bg-sys-chartreuse-25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sys-chartreuse-600 group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:ring-2 group-data-[focused=true]/day:ring-sys-chartreuse-600",
  selectedSingle: "data-[selected-single=true]:bg-sys-black-850 data-[selected-single=true]:text-sys-white data-[selected-single=true]:hover:bg-sys-black-850",
  rangeStart: "data-[range-start=true]:rounded-r-none data-[range-start=true]:bg-sys-black-850 data-[range-start=true]:text-sys-white data-[range-start=true]:hover:bg-sys-black-850",
  rangeMiddle: "data-[range-middle=true]:rounded-none data-[range-middle=true]:bg-sys-chartreuse-100 data-[range-middle=true]:hover:bg-sys-chartreuse-200",
  rangeEnd: "data-[range-end=true]:rounded-l-none data-[range-end=true]:bg-sys-black-850 data-[range-end=true]:text-sys-white data-[range-end=true]:hover:bg-sys-black-850",
  today: "data-[today=true]:bg-sys-neutral-600 data-[today=true]:font-bold data-[today=true]:data-[selected-single=true]:bg-sys-black-850",
  outside: "data-[outside=true]:text-sys-grey-500 data-[outside=true]:opacity-70",
  disabled: "disabled:pointer-events-none disabled:text-sys-grey-200 disabled:opacity-70",
} as const;

export const CALENDAR_CHEVRON_CLASS_MAP = {
  base: "h-4 w-4 text-sys-black-850",
  disabled: "text-sys-grey-200",
} as const;

export const CALENDAR_WEEK_NUMBER_CLASS_MAP = {
  cell: "h-8 w-8 p-0 text-center",
  content: "flex h-8 w-8 items-center justify-center rounded-sys-md text-sys-xs text-sys-grey-500",
} as const;

export const CALENDAR_CHEVRON_ICON_MAP = {
  up: "chevronUp",
  down: "chevronDown",
  left: "chevronLeft",
  right: "chevronRight",
} as const satisfies Record<TCalendarChevronOrientation, TIconKey>;

/*-------------------------------------
|  Types
|-------------------------------------*/

export type TCalendarProps = DayPickerProps;
export type TCalendarSingleProps = Extract<
  TCalendarProps,
  { mode: "single"; required?: false }
>;
export type TCalendarRootProps = RootProps;
export type TCalendarChevronProps = ChevronProps;
export type TCalendarDayButtonProps = DayButtonProps;
export type TCalendarWeekNumberProps = WeekNumberProps;
export type TCalendarChevronOrientation = NonNullable<
  TCalendarChevronProps["orientation"]
>;

type TCalendarDefaultProps = DefinedDefaults<
  TCalendarProps,
  "captionLayout" | "showOutsideDays"
>;

/*-------------------------------------
|  Defaults
|-------------------------------------*/

export const CALENDAR_DEFAULTS = {
  captionLayout: "label",
  showOutsideDays: true,
} satisfies TCalendarDefaultProps;
